import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Truck, Phone, MessageSquare, MapPin, Send, 
  Clock, CheckCircle, ChevronRight, Loader2, AlertCircle,
  Package, ShoppingBag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../services/api';
import { toast } from 'sonner';
import DeliveryCountdown from '../components/DeliveryCountdown';
import MaxDeliveryAssistant from '../components/MaxDeliveryAssistant';
import SnapchatSection from '../components/SnapchatSection';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_loyaltyhub-3/artifacts/bd853roq_La%20petite%20e%CC%81picerie-logo.png";

export default function LivraisonPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [zones, setZones] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showMaxAssistant, setShowMaxAssistant] = useState(false); // 🛵 Max Assistant
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    zone: '',
    items: '',
    totalAmount: '',
    slot: '',
    notes: '',
    paymentMethod: 'cash'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [zonesData, slotsData] = await Promise.all([
        api.getDeliveryZones(),
        api.getDeliverySlots()
      ]);
      setZones(zonesData.zones || []);
      setSlots(slotsData.slots || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      // Fallback data
      setZones([
        { id: 'zone_proche', name: 'Zone Proche', cities: ['Vermelles', 'Noyelles-lès-Vermelles', 'Annequin'], minOrder: 20, deliveryFee: 0 },
        { id: 'zone_moyenne', name: 'Zone Moyenne', cities: ['Beuvry', 'Labourse', 'Sailly-Labourse', 'Cambrin'], minOrder: 30, deliveryFee: 0 },
        { id: 'zone_eloignee', name: 'Zone Éloignée', cities: ['Béthune', 'Lens', 'Liévin', 'Autres'], minOrder: 40, deliveryFee: 0 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const selectedZone = zones.find(z => z.id === formData.zone);
  const minOrder = selectedZone?.minOrder || 20;

  const validateStep1 = () => {
    if (!formData.customerName.trim()) {
      setError('Entrez votre nom');
      return false;
    }
    if (!formData.customerPhone.trim()) {
      setError('Entrez votre téléphone');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    if (!formData.zone) {
      setError('Sélectionnez une zone');
      return false;
    }
    if (!formData.customerAddress.trim()) {
      setError('Entrez votre adresse');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep3 = () => {
    // Effacer l'erreur précédente d'abord
    setError('');
    
    if (!formData.items.trim()) {
      setError('Décrivez ce que vous voulez commander');
      return false;
    }
    const amount = parseFloat(formData.totalAmount);
    if (isNaN(amount) || amount < minOrder) {
      setError(`Minimum de commande: ${minOrder}€`);
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!formData.slot) {
      setError('Sélectionnez un créneau');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3 && validateStep3()) setStep(4);
    else if (step === 4 && validateStep4()) handleSubmit();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      // Utiliser l'endpoint /delivery/request qui fonctionne
      await api.createDeliveryRequest({
        nom: formData.customerName.split(' ').slice(1).join(' ') || formData.customerName,
        prenom: formData.customerName.split(' ')[0],
        telephone: formData.customerPhone,
        adresse: formData.customerAddress,
        zone: formData.zone,
        zoneLabel: selectedZone?.name || '',
        minimumCommande: minOrder,
        commentaire: `🛒 ${formData.items}\n\n💰 Montant estimé: ${formData.totalAmount}€\n🕐 Créneau: ${formData.slot}\n💳 Paiement: ${formData.paymentMethod === 'card' ? 'Carte' : 'Espèces'}${formData.notes ? '\n📝 Notes: ' + formData.notes : ''}`,
        modePaiement: formData.paymentMethod === 'card' ? 'carte' : 'especes',
        type: 'delivery'
      });
      setSuccess(true);
      toast.success('🎉 Commande envoyée !');
    } catch (error) {
      console.error('Erreur commande:', error);
      setError(error.response?.data?.detail || 'Erreur lors de la commande. Appelez-nous !');
      toast.error('Erreur - Appelez le 07 80 64 89 76');
    } finally {
      setSubmitting(false);
    }
  };

  // Page de succès
  if (success) {
    return (
      <div 
        className="min-h-screen overflow-x-hidden overflow-y-auto flex items-center justify-center p-4"
        style={{ background: 'linear-gradient(135deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)' }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-md text-center"
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="text-8xl mb-6"
          >
            🚚
          </motion.div>
          <h1 className="text-3xl font-bold text-white mb-4">Commande envoyée !</h1>
          <div 
            className="p-6 rounded-2xl mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.2) 0%, rgba(0, 255, 136, 0.2) 100%)',
              border: '2px solid #00FFFF',
              boxShadow: '0 0 30px rgba(0, 255, 255, 0.3)'
            }}
          >
            <p className="text-gray-300 mb-2">Un livreur vous contactera au</p>
            <p className="text-cyan-400 font-bold text-xl">{formData.customerPhone}</p>
            <p className="text-gray-400 text-sm mt-4">
              📦 {formData.items.slice(0, 50)}...
            </p>
            <p className="text-white font-bold mt-2">
              🕐 Créneau : {formData.slot}
            </p>
          </div>
          <div className="space-y-3">
            <Button
              onClick={() => {
                setSuccess(false);
                setStep(1);
                setFormData({
                  customerName: '',
                  customerPhone: '',
                  customerAddress: '',
                  zone: '',
                  items: '',
                  totalAmount: '',
                  slot: '',
                  notes: '',
                  paymentMethod: 'cash'
                });
              }}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-green-500"
            >
              Nouvelle commande
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full py-3 border-white/30 text-white"
            >
              Retour à l&apos;accueil
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen overflow-x-hidden overflow-y-auto p-4"
      style={{ background: 'linear-gradient(135deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)' }}
    >
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-4"
        >
          <Button
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}
            variant="ghost"
            className="text-cyan-400"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {step > 1 ? 'Retour' : 'Accueil'}
          </Button>
          <motion.img 
            src={LOGO_URL} 
            alt="LPE" 
            className="h-14"
            animate={{ scale: [1, 1.05, 1], rotate: [0, 2, -2, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            style={{ filter: 'drop-shadow(0 0 15px rgba(0,255,255,0.6))' }} 
          />
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mb-6"
        >
          <motion.div
            animate={{ 
              boxShadow: ['0 0 20px rgba(0,255,255,0.3)', '0 0 50px rgba(0,255,255,0.8)', '0 0 20px rgba(0,255,255,0.3)']
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="inline-block p-3 rounded-full mb-3"
            style={{ background: 'rgba(0, 255, 255, 0.1)' }}
          >
            <Truck className="w-12 h-12 text-cyan-400" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white mb-1">🚚 Je me fais livrer !</h1>
          <motion.p 
            animate={{ textShadow: ['0 0 5px #0ff', '0 0 20px #0ff', '0 0 5px #0ff'] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-xl font-bold text-cyan-400"
          >
            LIVRAISON GRATUITE 🎉
          </motion.p>
          <p className="text-gray-400 text-sm">🌙 Ramadan • Ven & Sam • 21h-4h du matin</p>
        </motion.div>

        {/* 🛵 GROS BOUTON MAX - Commander avec l'assistant ! */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <motion.button
            onClick={() => setShowMaxAssistant(true)}
            className="w-full py-5 px-6 rounded-2xl font-bold text-xl flex items-center justify-center gap-4 text-white relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #FF8C00 0%, #FFA500 50%, #FFB347 100%)',
              border: '3px solid #FF8C00',
              boxShadow: '0 0 30px rgba(255, 140, 0, 0.5)'
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            animate={{
              boxShadow: [
                '0 0 20px rgba(255, 140, 0, 0.4)',
                '0 0 50px rgba(255, 140, 0, 0.8)',
                '0 0 20px rgba(255, 140, 0, 0.4)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {/* Avatar Max */}
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white/50">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/90b8cfd5-4d07-402e-9a12-a0e9be89c0f2/images/d2dd79574adadc4218e7bf647409093e50bc748604d8f603eea8192b674d4867.png"
                alt="Max"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span>Commander avec Max</span>
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                >
                  🛵
                </motion.span>
              </div>
              <p className="text-sm font-normal text-white/80">L'assistant qui vous guide pas à pas !</p>
            </div>
            <ChevronRight className="w-6 h-6 ml-auto" />
          </motion.button>
          
          <p className="text-center text-gray-400 text-xs mt-2">
            Ou remplissez le formulaire ci-dessous 👇
          </p>
        </motion.div>

        {/* Countdown Timer */}
        <DeliveryCountdown openingHour={21} closingHour={3} />

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                    s < step ? 'bg-green-500 text-white' :
                    s === step ? 'bg-cyan-500 text-white ring-4 ring-cyan-500/30' :
                    'bg-white/20 text-gray-400'
                  }`}
                >
                  {s < step ? '✓' : s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-1 rounded ${s < step ? 'bg-green-500' : 'bg-white/20'}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-gray-400 text-xs">
            {step === 1 && '📱 Vos coordonnées'}
            {step === 2 && '📍 Votre adresse'}
            {step === 3 && '🛒 Votre commande'}
            {step === 4 && '🕐 Créneau horaire'}
          </p>
        </div>

        {/* Form Card */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="backdrop-blur-xl rounded-3xl p-6 border-2"
          style={{
            background: 'rgba(10, 0, 30, 0.9)',
            borderColor: '#00FFFF',
            boxShadow: '0 0 40px rgba(0, 255, 255, 0.3)'
          }}
        >
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 mx-auto text-cyan-400 animate-spin" />
              <p className="text-gray-400 mt-4">Chargement...</p>
            </div>
          ) : (
            <>
              {/* Step 1: Contact Info */}
              {step === 1 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Phone className="w-5 h-5 text-cyan-400" />
                    Vos coordonnées
                  </h2>
                  <div>
                    <label className="text-white text-sm mb-2 block">Votre nom *</label>
                    <Input
                      value={formData.customerName}
                      onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                      placeholder="Prénom Nom"
                      className="bg-white/10 border-white/20 text-white h-12 text-lg"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm mb-2 block">Téléphone *</label>
                    <Input
                      value={formData.customerPhone}
                      onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                      placeholder="06 XX XX XX XX"
                      className="bg-white/10 border-white/20 text-white h-12 text-lg"
                    />
                  </div>
                  
                  {/* Quick call option */}
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-gray-400 text-sm text-center mb-3">Ou appelez-nous directement :</p>
                    <a
                      href="tel:0780648976"
                      className="flex items-center justify-center gap-2 p-3 rounded-xl bg-green-500/20 border border-green-500/50 text-green-400 font-bold"
                    >
                      <Phone className="w-5 h-5" />
                      07 80 64 89 76
                    </a>
                  </div>
                </div>
              )}

              {/* Step 2: Address */}
              {step === 2 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                    Votre adresse
                  </h2>
                  
                  <div>
                    <label className="text-white text-sm mb-2 block">📍 Sélectionnez votre zone</label>
                    <div className="space-y-2">
                      {zones.map((zone) => (
                        <button
                          key={zone.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, zone: zone.id })}
                          className={`w-full p-4 rounded-xl text-left transition-all ${
                            formData.zone === zone.id
                              ? 'ring-2 ring-cyan-500 bg-cyan-500/20'
                              : 'bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-white font-semibold">{zone.name}</p>
                              <p className="text-gray-400 text-xs">{zone.cities?.join(', ')}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-cyan-400 font-bold">{zone.minOrder}€ min</p>
                              <p className="text-green-400 text-xs font-semibold">GRATUIT 🎉</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-white text-sm mb-2 block">🏠 Adresse complète *</label>
                    <textarea
                      value={formData.customerAddress}
                      onChange={(e) => setFormData({ ...formData, customerAddress: e.target.value })}
                      placeholder="Numéro, rue, code postal, ville..."
                      className="w-full p-3 rounded-xl bg-white/10 text-white border border-white/20 resize-none h-24"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Order */}
              {step === 3 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-cyan-400" />
                    Votre commande
                  </h2>
                  
                  <div>
                    <label className="text-white text-sm mb-2 block">🛒 Que voulez-vous ? *</label>
                    <textarea
                      value={formData.items}
                      onChange={(e) => setFormData({ ...formData, items: e.target.value })}
                      placeholder="Ex: 1 pack Heineken, 2 bouteilles de rosé, 1 paquet de chips..."
                      className="w-full p-3 rounded-xl bg-white/10 text-white border border-white/20 resize-none h-32"
                    />
                  </div>

                  <div>
                    <label className="text-white text-sm mb-2 block flex items-center gap-2">
                      <span>📊 Montant estimé de votre commande *</span>
                    </label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={formData.totalAmount}
                        onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
                        placeholder={`Minimum ${minOrder}€ pour votre zone`}
                        className="bg-white/10 border-white/20 text-white h-12 text-lg pr-10"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                    </div>
                    <div className="mt-2 p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                      <p className="text-cyan-400 text-sm font-medium">
                        ✓ {selectedZone?.name || 'Zone'} : Minimum {minOrder}€ • Livraison GRATUITE 🎉
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-white text-sm mb-2 block">💳 Paiement</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, paymentMethod: 'cash' })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.paymentMethod === 'cash'
                            ? 'border-green-500 bg-green-500/20 text-green-400'
                            : 'border-white/20 bg-white/5 text-gray-400'
                        }`}
                      >
                        <span className="text-2xl block mb-1">💵</span>
                        <span className="font-semibold">Espèces</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, paymentMethod: 'card' })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.paymentMethod === 'card'
                            ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                            : 'border-white/20 bg-white/5 text-gray-400'
                        }`}
                      >
                        <span className="text-2xl block mb-1">💳</span>
                        <span className="font-semibold">Carte</span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-white text-sm mb-2 block">📝 Notes (optionnel)</label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Instructions particulières..."
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Time Slot */}
              {step === 4 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-cyan-400" />
                    Créneau de livraison
                  </h2>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {slots.length > 0 ? slots.map((slot) => (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => slot.available && setFormData({ ...formData, slot: slot.time })}
                        disabled={!slot.available}
                        className={`p-3 rounded-xl text-center transition-all ${
                          formData.slot === slot.time
                            ? 'ring-2 ring-cyan-500 bg-cyan-500/20'
                            : slot.available
                              ? 'bg-white/5 hover:bg-white/10'
                              : 'bg-red-500/10 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <p className={`font-semibold ${
                          formData.slot === slot.time ? 'text-cyan-400' : 
                          slot.available ? 'text-white' : 'text-red-400'
                        }`}>
                          {slot.time}
                        </p>
                        <p className={`text-xs ${slot.available ? 'text-gray-400' : 'text-red-400'}`}>
                          {slot.available ? `${slot.remaining} places` : 'Complet'}
                        </p>
                      </button>
                    )) : (
                      // Fallback slots - RAMADAN EDITION (21h-4h)
                      ['21:00-21:30', '21:30-22:00', '22:00-22:30', '22:30-23:00', '23:00-23:30', '23:30-00:00', '00:00-00:30', '00:30-01:00', '01:00-01:30', '01:30-02:00', '02:00-02:30', '02:30-03:00', '03:00-03:30', '03:30-04:00'].map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setFormData({ ...formData, slot: time })}
                          className={`p-3 rounded-xl text-center transition-all ${
                            formData.slot === time
                              ? 'ring-2 ring-cyan-500 bg-cyan-500/20'
                              : 'bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          <p className={`font-semibold ${formData.slot === time ? 'text-cyan-400' : 'text-white'}`}>
                            {time}
                          </p>
                        </button>
                      ))
                    )}
                  </div>

                  {/* Recap */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 mt-4">
                    <h4 className="text-white font-semibold mb-3">📋 Récapitulatif</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Client</span>
                        <span className="text-white">{formData.customerName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Téléphone</span>
                        <span className="text-white">{formData.customerPhone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Zone</span>
                        <span className="text-white">{selectedZone?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Montant</span>
                        <span className="text-cyan-400 font-bold">{formData.totalAmount}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Paiement</span>
                        <span className="text-white">{formData.paymentMethod === 'cash' ? '💵 Espèces' : '💳 Carte'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-4 p-3 rounded-xl bg-red-500/20 border border-red-500/50 flex items-center gap-2"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </motion.div>
              )}

              {/* Navigation Buttons */}
              <div className="mt-6">
                <motion.div
                  animate={{ boxShadow: ['0 0 10px rgba(0,255,255,0.3)', '0 0 30px rgba(0,255,255,0.7)', '0 0 10px rgba(0,255,255,0.3)'] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="rounded-xl"
                >
                  <Button
                    onClick={handleNext}
                    disabled={submitting}
                    className="w-full h-14 text-lg font-bold rounded-xl"
                    style={{
                      background: 'linear-gradient(135deg, #00FFFF 0%, #00FF88 100%)',
                      color: '#000'
                    }}
                  >
                    {submitting ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : step === 4 ? (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Envoyer ma commande
                      </>
                    ) : (
                      <>
                        Suivant
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </motion.div>
              </div>
            </>
          )}
        </motion.div>

        {/* Bottom info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-6 text-center"
        >
          <p className="text-gray-500 text-xs mb-2">
            Questions ? Appelez-nous !
          </p>
          <a
            href="tel:0780648976"
            className="inline-flex items-center gap-2 text-cyan-400 font-bold"
          >
            <Phone className="w-4 h-4" />
            07 80 64 89 76
          </a>
        </motion.div>
      </div>
      
      {/* 👻 Section Snapchat - Version discrète */}
      <SnapchatSection compact={true} className="bg-gradient-to-b from-transparent to-cyan-900/10" />
      
      {/* 🛵 Max - Assistant Livraison (Bouton flottant) */}
      <motion.button
        onClick={() => setShowMaxAssistant(true)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-lg flex items-center justify-center overflow-hidden"
        style={{
          border: '3px solid rgba(255, 165, 0, 0.7)',
          boxShadow: '0 0 30px rgba(255, 165, 0, 0.5), 0 4px 20px rgba(0,0,0,0.4)'
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          boxShadow: [
            '0 0 20px rgba(255, 165, 0, 0.4), 0 4px 15px rgba(0,0,0,0.3)',
            '0 0 40px rgba(255, 165, 0, 0.7), 0 4px 25px rgba(0,0,0,0.4)',
            '0 0 20px rgba(255, 165, 0, 0.4), 0 4px 15px rgba(0,0,0,0.3)'
          ]
        }}
        transition={{ duration: 2, repeat: Infinity }}
        data-testid="max-assistant-button"
      >
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/90b8cfd5-4d07-402e-9a12-a0e9be89c0f2/images/d2dd79574adadc4218e7bf647409093e50bc748604d8f603eea8192b674d4867.png" 
          alt="Max le livreur" 
          className="w-full h-full object-cover"
        />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
      </motion.button>
      
      {/* 🛵 Max Delivery Assistant (Popup) */}
      <MaxDeliveryAssistant
        isOpen={showMaxAssistant}
        onClose={() => setShowMaxAssistant(false)}
        onOrderSubmit={() => {
          setShowMaxAssistant(false);
          setSuccess(true);
          toast.success('🛵 Commande envoyée avec Max !');
        }}
      />
    </div>
  );
}
